import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const questions = [
  {
    id: 'q1',
    label: 'What best describes you?',
    summaryLabel: 'Who you are',
    options: [
      'I am a business owner / entrepreneur',
      'I represent a company or corporate house',
      'I am an investor looking for Africa opportunities'
    ]
  },
  {
    id: 'q2',
    label: 'Which sector are you interested in exploring in Africa?',
    summaryLabel: 'Sector interest',
    options: [
      'Mining, minerals or natural resources',
      'Manufacturing, FMCG or consumer goods',
      'Agriculture, dairy or food processing',
      'Infrastructure, real estate or energy',
      'I am open to exploring, not fixed on a sector yet'
    ]
  },
  {
    id: 'q3',
    label: 'What is your current stage of planning?',
    summaryLabel: 'Planning stage',
    options: [
      'I am actively planning and ready to move in the next 6 months',
      'I am researching and want expert guidance before deciding',
      'I am at an early stage and just exploring the opportunity'
    ]
  },
  {
    id: 'q4',
    label: 'Have you visited Africa or done any prior business there?',
    summaryLabel: 'Africa experience',
    options: [
      'Yes, I have visited or have some existing connections in Africa',
      'No, but I have done my research and am serious about it',
      'No, I am completely new to Africa as a market'
    ]
  },
  {
    id: 'q5',
    label: 'What kind of support are you primarily looking for?',
    summaryLabel: 'Support needed',
    options: [
      'Strategy and direction, understanding where and how to start',
      'Connections and introductions to the right people in Africa',
      'Complete handholding, from planning all the way to setting up on ground'
    ]
  }
];

export const LeadFormPage = () => {
  const [showContactForm, setShowContactForm] = useState(true);
  const [contactDetails, setContactDetails] = useState({
    fullName: '',
    email: '',
    phone: '',
    city: '',
    company: '',
    designation: ''
  });
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState('');

  const totalSteps = questions.length + 1; // +1 for contact form
  const progress = showContactForm ? (1 / totalSteps) * 100 : ((currentStep + 2) / totalSteps) * 100;

  const handleContactChange = (e) => {
    setContactDetails(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const validateContact = () => {
    if (!contactDetails.fullName.trim()) {
      setError('Please enter your full name');
      return false;
    }
    if (!contactDetails.email.trim() || !contactDetails.email.includes('@')) {
      setError('Please enter a valid email address');
      return false;
    }
    if (!contactDetails.phone.trim() || contactDetails.phone.length < 10) {
      setError('Please enter a valid phone number');
      return false;
    }
    return true;
  };

  const handleContactNext = () => {
    if (validateContact()) {
      setShowContactForm(false);
      setError('');
    }
  };

  const handleSelect = (questionId, option) => {
    setAnswers(prev => ({ ...prev, [questionId]: option }));
    setError('');
  };

  const handleNext = async () => {
    const currentQuestion = questions[currentStep];
    if (!answers[currentQuestion.id]) {
      setError('Please select an option to continue.');
      return;
    }

    if (currentStep < questions.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      await handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
      setError('');
    } else {
      setShowContactForm(true);
      setError('');
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/lead-form`, {
        fullName: contactDetails.fullName,
        email: contactDetails.email,
        phone: contactDetails.phone,
        city: contactDetails.city,
        company: contactDetails.company,
        designation: contactDetails.designation,
        q1: answers.q1,
        q2: answers.q2,
        q3: answers.q3,
        q4: answers.q4,
        q5: answers.q5
      });
      setIsComplete(true);
      toast.success('Thank you! Your response has been submitted.');
    } catch (err) {
      console.error('Submission error:', err);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isComplete) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-lg bg-[#111] border border-white/10 rounded-2xl p-8"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#D4AF37]/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check size={32} className="text-[#D4AF37]" />
            </div>
            <h2 className="text-2xl font-bold text-white font-['Playfair_Display'] mb-2">
              Thank you, {contactDetails.fullName}!
            </h2>
            <p className="text-white/60">
              Your response has been submitted. Kirit's team will review your answers and reach out shortly.
            </p>
          </div>

          <div className="space-y-4 bg-white/5 rounded-xl p-4">
            <p className="text-xs text-[#D4AF37] font-semibold tracking-wider mb-3">YOUR RESPONSES</p>
            
            {/* Contact Summary */}
            <div className="border-b border-white/10 pb-3">
              <p className="text-xs text-white/40 mb-1">Contact</p>
              <p className="text-sm text-white/80">{contactDetails.fullName} · {contactDetails.email}</p>
            </div>
            
            {questions.map((q) => (
              <div key={q.id} className="border-b border-white/10 pb-3 last:border-0">
                <p className="text-xs text-white/40 mb-1">{q.summaryLabel}</p>
                <p className="text-sm text-white/80">{answers[q.id]}</p>
              </div>
            ))}
          </div>

          <div className="mt-6 text-center">
            <a
              href="https://kiritsobti.com"
              className="text-[#D4AF37] text-sm hover:underline"
            >
              Visit kiritsobti.com →
            </a>
          </div>
        </motion.div>
      </div>
    );
  }

  // Contact Details Form (Section A)
  if (showContactForm) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <div className="w-full max-w-2xl">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-6"
          >
            <h1 className="text-2xl sm:text-3xl font-bold text-white font-['Playfair_Display'] mb-2">
              Tell us about your <span className="text-[#D4AF37]">Africa ambitions</span>
            </h1>
            <p className="text-white/60 text-sm">
              Answer a few quick questions so Kirit can understand your goals and recommend the right path forward.
            </p>
          </motion.div>

          {/* Progress */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-white/60 text-sm">Page 1 of 6</span>
              <span className="text-[#D4AF37] text-sm font-semibold">{Math.round(progress)}%</span>
            </div>
            <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-[#D4AF37]"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
          </div>

          {/* Contact Form Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#111] border border-white/10 rounded-2xl p-6 sm:p-8"
          >
            <p className="text-xs text-[#D4AF37] font-semibold tracking-wider mb-2">SECTION A</p>
            <h2 className="text-xl sm:text-2xl font-bold text-white font-['Playfair_Display'] mb-6">
              Your Contact Details
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* Full Name */}
              <div>
                <label className="block text-white/60 text-sm mb-2">
                  Full Name <span className="text-[#D4AF37]">*</span>
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={contactDetails.fullName}
                  onChange={handleContactChange}
                  placeholder="Enter your full name"
                  className="w-full px-0 py-3 bg-transparent border-b border-white/20 text-white placeholder:text-white/30 text-base focus:outline-none focus:border-[#D4AF37] transition-colors"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-white/60 text-sm mb-2">
                  Email Address <span className="text-[#D4AF37]">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={contactDetails.email}
                  onChange={handleContactChange}
                  placeholder="your@email.com"
                  className="w-full px-0 py-3 bg-transparent border-b border-white/20 text-white placeholder:text-white/30 text-base focus:outline-none focus:border-[#D4AF37] transition-colors"
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-white/60 text-sm mb-2">
                  Phone / WhatsApp <span className="text-[#D4AF37]">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={contactDetails.phone}
                  onChange={handleContactChange}
                  placeholder="+91 XXXXX XXXXX"
                  className="w-full px-0 py-3 bg-transparent border-b border-white/20 text-white placeholder:text-white/30 text-base focus:outline-none focus:border-[#D4AF37] transition-colors"
                />
              </div>

              {/* City */}
              <div>
                <label className="block text-white/60 text-sm mb-2">
                  City
                </label>
                <input
                  type="text"
                  name="city"
                  value={contactDetails.city}
                  onChange={handleContactChange}
                  placeholder="Your city"
                  className="w-full px-0 py-3 bg-transparent border-b border-white/20 text-white placeholder:text-white/30 text-base focus:outline-none focus:border-[#D4AF37] transition-colors"
                />
              </div>

              {/* Company */}
              <div>
                <label className="block text-white/60 text-sm mb-2">
                  Company / Business Name
                </label>
                <input
                  type="text"
                  name="company"
                  value={contactDetails.company}
                  onChange={handleContactChange}
                  placeholder="Company name"
                  className="w-full px-0 py-3 bg-transparent border-b border-white/20 text-white placeholder:text-white/30 text-base focus:outline-none focus:border-[#D4AF37] transition-colors"
                />
              </div>

              {/* Designation */}
              <div>
                <label className="block text-white/60 text-sm mb-2">
                  Designation / Role
                </label>
                <input
                  type="text"
                  name="designation"
                  value={contactDetails.designation}
                  onChange={handleContactChange}
                  placeholder="Your role"
                  className="w-full px-0 py-3 bg-transparent border-b border-white/20 text-white placeholder:text-white/30 text-base focus:outline-none focus:border-[#D4AF37] transition-colors"
                />
              </div>
            </div>

            {error && (
              <p className="text-red-400 text-sm mt-4">{error}</p>
            )}

            {/* Next Button */}
            <div className="flex justify-end mt-8 pt-6 border-t border-white/10">
              <button
                onClick={handleContactNext}
                className="flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-[#0A0A0A] font-semibold rounded-full hover:bg-[#E5C048] transition-all"
              >
                NEXT
                <ChevronRight size={18} />
              </button>
            </div>
          </motion.div>

          {/* Footer */}
          <div className="text-center mt-8">
            <p className="text-white/30 text-xs">
              Powered by <a href="https://kiritsobti.com" className="text-[#D4AF37] hover:underline">kiritsobti.com</a> · Your responses are confidential
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Questions Section
  const currentQuestion = questions[currentStep];

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-6"
        >
          <h1 className="text-2xl sm:text-3xl font-bold text-white font-['Playfair_Display'] mb-2">
            Tell us about your <span className="text-[#D4AF37]">Africa ambitions</span>
          </h1>
          <p className="text-white/60 text-sm">
            Answer 5 quick questions so Kirit can understand your goals and recommend the right path forward.
          </p>
          <p className="text-white/40 text-xs mt-2">
            All questions are required · Takes less than 2 minutes
          </p>
        </motion.div>

        {/* Progress */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-white/60 text-sm">Page {currentStep + 2} of 6</span>
            <span className="text-[#D4AF37] text-sm font-semibold">{Math.round(progress)}%</span>
          </div>
          <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-[#D4AF37]"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </div>

        {/* Question Card */}
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className="bg-[#111] border border-white/10 rounded-2xl p-6"
        >
          <p className="text-white font-semibold mb-4">
            {currentQuestion.label} <span className="text-[#D4AF37]">*</span>
          </p>

          <div className="space-y-3">
            {currentQuestion.options.map((option, idx) => (
              <button
                key={idx}
                onClick={() => handleSelect(currentQuestion.id, option)}
                className={`w-full text-left p-4 rounded-xl border transition-all ${
                  answers[currentQuestion.id] === option
                    ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-white'
                    : 'border-white/10 bg-white/5 text-white/70 hover:border-white/30 hover:bg-white/10'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    answers[currentQuestion.id] === option
                      ? 'border-[#D4AF37] bg-[#D4AF37]'
                      : 'border-white/30'
                  }`}>
                    {answers[currentQuestion.id] === option && (
                      <Check size={12} className="text-[#0A0A0A]" />
                    )}
                  </div>
                  <span className="text-sm">{option}</span>
                </div>
              </button>
            ))}
          </div>

          {error && (
            <p className="text-red-400 text-sm mt-3">{error}</p>
          )}
        </motion.div>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-6">
          <button
            onClick={handleBack}
            disabled={currentStep === 0}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
              currentStep === 0
                ? 'text-white/30 cursor-not-allowed'
                : 'text-white/70 hover:text-white hover:bg-white/10'
            }`}
          >
            <ChevronLeft size={18} />
            Back
          </button>

          {/* Step Dots */}
          <div className="flex gap-2">
            {questions.map((_, idx) => (
              <div
                key={idx}
                className={`w-2 h-2 rounded-full transition-all ${
                  idx === currentStep
                    ? 'bg-[#D4AF37] w-4'
                    : idx < currentStep
                    ? 'bg-[#D4AF37]/50'
                    : 'bg-white/20'
                }`}
              />
            ))}
          </div>

          <button
            onClick={handleNext}
            disabled={isSubmitting}
            className="flex items-center gap-2 px-5 py-2 bg-[#D4AF37] text-[#0A0A0A] font-semibold rounded-lg hover:bg-[#E5C048] transition-all disabled:opacity-50"
          >
            {isSubmitting ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Submitting...
              </>
            ) : currentStep === questions.length - 1 ? (
              <>
                Submit
                <Check size={18} />
              </>
            ) : (
              <>
                Next
                <ChevronRight size={18} />
              </>
            )}
          </button>
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-white/30 text-xs">
            Powered by <a href="https://kiritsobti.com" className="text-[#D4AF37] hover:underline">kiritsobti.com</a> · Your responses are confidential
          </p>
        </div>
      </div>
    </div>
  );
};

export default LeadFormPage;
